<!--
/**
 * FormKitEmployeeSelect
 *
 * Custom FormKit input that wraps PrimeVue AutoComplete for employee selection.
 * Fetches options via useBffClient().getEmployees() on mount.
 * Supports dual mode: inside a FormKit form (context) or standalone with v-model.
 *
 * @description Employee autocomplete with async loading from BFF.
 *
 * FormKit usage (e.g. in a form):
 *   <FormKit type="employeeSelect" name="ownerId" label="Owner"
 *            placeholder="Select an employee..." :showClear="true"
 *            :role-ids="['role-id-1', 'role-id-2']" />
 *
 * Standalone usage (e.g. in a card/record view):
 *   <FormKitEmployeeSelect v-model="selectedEmployeeId" placeholder="Select employee..."
 *                           :show-clear="true" :role-ids="grantOwnerRoleIds" />
 *
 * @prop {Object} [context] - FormKit framework context (provided when used as FormKit input).
 *   Context may include: placeholder, disabled, showClear, activeOnly, requireLicence, roleIds.
 * @prop {string|null} [modelValue] - Selected employee id.
 * @prop {string} [placeholder=''] - Placeholder text when nothing selected. Empty = non-breaking space.
 * @prop {boolean} [showClear=false] - Show clear button when a value is selected.
 * @prop {boolean} [disabled=false] - Disable the input.
 * @prop {boolean} [activeOnly=true] - Only show people who work at FP.
 *   People who've left, when included, are suffixed with "(LEFT)".
 * @prop {boolean} [requireLicence=false] - Only show people with an active Salesforce licence,
 *   for fields that sync to a Salesforce owner.
 * @prop {string[]} [roleIds=[]] - Only show employees with at least one of these role IDs.
 * @emits {string} update:modelValue - Emitted with the selected employee id.
 */
-->
<template>
  <div v-bind="attrs" :aria-required="isRequired" :class="wrapperClass">
    <AutoComplete
      ref="autoCompleteRef"
      :input-id="inputId"
      :model-value="displayValue"
      :suggestions="suggestions"
      option-label="label"
      option-value="id"
      :placeholder="displayPlaceholder"
      :disabled="disabled"
      :loading="loading"
      :show-clear="showClear"
      :pt="autoCompletePt"
      complete-on-focus
      class="w-full"
      data-testid="formkit-employee-select-input"
      @complete="onSearch"
      @update:model-value="onSelect"
    />
  </div>
</template>

<script lang="ts" setup>
import type { FormKitFrameworkContext } from '@formkit/core'
import AutoComplete from 'primevue/autocomplete'
import type { PropType } from 'vue'
import { computed, ref } from 'vue'
import { useEmployees } from '~/composables/useEmployees'
import {
  useFormKitAutoComplete,
  type AutoCompleteOption,
} from '~/composables/useFormKitAutoComplete'
import type { Employee } from '~/models/graphql/generated/types'

type EmployeeAutoCompleteRef = {
  show?: () => void
  input?: HTMLInputElement
  $el?: HTMLElement
}

const autoCompleteRef = ref<EmployeeAutoCompleteRef>()

const props = defineProps({
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        placeholder?: string
        disabled?: boolean
        showClear?: boolean
        activeOnly?: boolean
        requireLicence?: boolean
        roleIds?: string[]
      }
    >,
    default: undefined,
  },
  modelValue: {
    type: String as PropType<string | null>,
    default: undefined,
  },
  placeholder: { type: String, default: '' },
  showClear: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  activeOnly: { type: Boolean, default: true },
  requireLicence: { type: Boolean, default: false },
  roleIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const { employees, loading, load } = useEmployees()

const activeOnly = computed(() => props.context?.activeOnly ?? props.activeOnly)
const requireLicence = computed(
  () => props.context?.requireLicence ?? props.requireLicence
)
const showClear = computed(() => props.context?.showClear ?? props.showClear)
const roleIds = computed(() => props.context?.roleIds ?? props.roleIds)

function employeeMatchesRoleIds(employee: Employee, ids: string[]): boolean {
  if (ids.length === 0) return true
  return employee.roles?.some((role) => ids.includes(role.id)) ?? false
}

const employeeOptions = computed(() => {
  const list = employees.value.filter((employee) => {
    if (activeOnly.value && !employee.worksAtFp) return false
    if (requireLicence.value && !employee.salesforceLicenceActive) return false
    return employeeMatchesRoleIds(employee, roleIds.value)
  })
  return list
    .map(optionFromEmployee)
    .sort((a, b) => a.label.localeCompare(b.label))
})

function optionFromEmployee(employee: Employee): AutoCompleteOption {
  const name =
    typeof employee.name === 'string' && employee.name.trim().length > 0
      ? employee.name.trim()
      : ''
  const displayName = name || '(No name)'
  const label =
    employee.worksAtFp === false ? `${displayName} (LEFT)` : displayName
  return { id: employee.id as string, label }
}

async function ensureEmployeesLoaded(): Promise<Employee[]> {
  if (employees.value.length > 0) return employees.value
  return load()
}

async function search(query: string): Promise<AutoCompleteOption[]> {
  await ensureEmployeesLoaded()

  const trimmed = query?.trim().toLowerCase()
  if (!trimmed) return employeeOptions.value

  return employeeOptions.value.filter((option) => {
    const employee = employees.value.find((e) => String(e.id) === option.id)
    const email =
      typeof employee?.email === 'string' ? employee.email.toLowerCase() : ''

    return (
      option.label.toLowerCase().includes(trimmed) || email.includes(trimmed)
    )
  })
}

async function loadById(id: string): Promise<AutoCompleteOption | null> {
  const list = await ensureEmployeesLoaded()
  const employee = list.find((e) => String(e.id) === id)
  return employee ? optionFromEmployee(employee) : null
}

const {
  attrs,
  inputId,
  disabled,
  isRequired,
  displayPlaceholder,
  displayValue,
  suggestions,
  onSearch,
  onSelect,
} = useFormKitAutoComplete({ props, emit, search, loadById })

const wrapperClass = computed(() => {
  const classes = ['w-full', 'formkit-primevue']
  if (showClear.value) classes.push('formkit-primevue--clearable')
  return classes.join(' ')
})

const autoCompletePt = computed(() => ({
  pcInputText: {
    root: {
      class: showClear.value ? 'pr-10' : 'pr-auto',
    },
  },
}))

defineExpose({
  show: () => {
    autoCompleteRef.value?.input?.focus()
    autoCompleteRef.value?.$el?.querySelector('input')?.focus()
    autoCompleteRef.value?.show?.()
  },
})
</script>
